import { useState } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import AddSingleStudent from './AddSingleStudent';
import AddMultipleStudents from './AddMultipleStudents';
import ModeSelector from '../../components/ModeSelector';
import Button from '../../components/Button';
import { Icon } from '@iconify/react';
import usersSolid from '@iconify/icons-clarity/users-solid';
import avatarSolid from '@iconify/icons-clarity/avatar-solid';
import styles from './AddStudents.module.scss';

interface Props {
  hideModal: () => void;
  defaultStudentPassword: string;
  studentNumberLimit: number;
  totalNumberOfStudents: number;
  onAddStudents: (arg0: Array<string>, arg1: Array<string>) => void;
}

interface FormValues {
  [key: string]: string;
}

const AddStudents = ({
  hideModal,
  defaultStudentPassword,
  studentNumberLimit,
  totalNumberOfStudents,
  onAddStudents,
}: Props): JSX.Element => {
  const [studentNames, setStudentNames] = useState<string[]>([]);
  const [studentPasswords, setStudentPasswords] = useState<string[]>([]);
  const [isAddMultiple, setIsAddMultiple] = useState(false);
  const addSingleInitialValues: FormValues = {
    studentName: '',
    password: defaultStudentPassword,
  };

  const multipleMax = Math.min(5, studentNumberLimit - totalNumberOfStudents);
  const arrayToReduce = Array.from(Array(multipleMax).keys());
  const addMultipleInitialValues: FormValues = arrayToReduce.reduce(
    (acc, _, currIndex) => ({
      ...acc,
      [`studentName${currIndex}`]: '',
      [`password${currIndex}`]: defaultStudentPassword,
    }),
    {},
  );
  const initialValues: FormValues = {
    ...addSingleInitialValues,
    ...addMultipleInitialValues,
  };

  const schemaValues = Object.keys(initialValues).reduce((acc, curr, currIndex) => {
    if (curr.includes('student')) {
      return {
        ...acc,
        [curr]: Yup.string().min(4, 'Please enter a student name (min 4 characters)'),
      };
    }
    if (curr.includes('pass')) {
      return {
        ...acc,
        [curr]: Yup.string().test('has name been entered', function (value: string | undefined) {
          if (this.parent[Object.keys(initialValues)[currIndex - 1]] !== '') {
            return value === '' || (value && value.length < 6)
              ? this.createError({
                  message: 'Minimum 6 characters',
                })
              : true;
          }
          return true;
        }),
      };
    }
    return acc;
  }, {});

  const validationSchema = Yup.object().shape(schemaValues);

  const handleAddStudent = async (
    values: FormValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  ) => {
    try {
      const newPasswordList = [...studentPasswords, values.password];
      const newStudentNames = [...studentNames, values.studentName];
      setStudentNames(newStudentNames);
      setStudentPasswords(newPasswordList);
    } finally {
      setFieldValue('studentName', '', false);
      setFieldValue('password', values.password || defaultStudentPassword, false);
    }
  };

  const handleRemoveStudent = (index: number) => {
    const newStudentNames = [...studentNames];
    const newPasswords = [...studentPasswords];
    newStudentNames.splice(index, 1);
    newPasswords.splice(index, 1);
    setStudentNames(newStudentNames);
    setStudentPasswords(newPasswords);
  };

  const handleSwitch = () => {
    setIsAddMultiple((prevState) => !prevState);
  };

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const { studentName, password, ...multiValues } = values;
    const multiValuesArray = Object.entries(multiValues);
    const multiNames: string[] = [];
    const multiPasswords: string[] = [];
    multiValuesArray.reduce((acc, [k, v], currIndex) => {
      if (k.includes('student') && v !== '') {
        multiNames.push(v.trim());
        multiPasswords.push(multiValuesArray[currIndex + 1][1].trim());
      }
      return acc;
    }, []);
    if (studentName === '' && studentNames.length === 0 && multiNames.length === 0) return null;
    setSubmitting(true);
    try {
      let namesToAdd = [];
      let passwordsToAdd = [];

      if (studentNames.length > 0) {
        namesToAdd = [...studentNames, ...multiNames];
        passwordsToAdd = [...studentPasswords, ...multiPasswords];
      } else if (studentName === '') {
        namesToAdd = multiNames;
        passwordsToAdd = multiPasswords;
      } else {
        namesToAdd = [studentName, ...multiNames];
        passwordsToAdd = [password, ...multiPasswords];
      }
      onAddStudents(namesToAdd, passwordsToAdd);
    } catch {
      console.error('Could not add student to class');
    } finally {
      setStudentNames([]);
      setStudentPasswords([]);
      setIsAddMultiple(false);
      hideModal();
      setSubmitting(false);
    }
  };

  const addSingleIcon = <Icon icon={avatarSolid} />;
  const addMultipleIcon = <Icon icon={usersSolid} />;

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <h2>Add Students</h2>
        <button onClick={hideModal}>x</button>
      </div>
      <div className={styles.content}>
        <div className={styles.selectorRow}>
          <ModeSelector
            isSecondOptionChecked={isAddMultiple}
            tooltip1='Add one student at a time'
            tooltip2='Add multiple students at a single time'
            leftContent={addSingleIcon}
            rightContent={addMultipleIcon}
            primaryColor={'#ffffff'}
            secondaryColor={'#00afd8'}
            borderColor={'#012a73'}
            handleSwitch={handleSwitch}
          />
        </div>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ values, validateForm, setFieldError, setFieldValue }) => (
            <Form className={styles.formContainer}>
              {!isAddMultiple && (
                <AddSingleStudent
                  values={values}
                  setFieldValue={setFieldValue}
                  studentNames={studentNames}
                  handleRemoveStudent={handleRemoveStudent}
                  handleAddStudent={handleAddStudent}
                  validateForm={validateForm}
                  setFieldError={setFieldError}
                />
              )}
              {isAddMultiple && <AddMultipleStudents values={values} />}
              <div className={styles.buttonRow}>
                {isAddMultiple && (
                  <p className={styles.noteForAddingMultipleStudent}>
                    Please note student passwords cannot be changed once added to your class.
                  </p>
                )}
                <Button type='submit' small darkBlue>
                  CONFIRM + ADD TO CLASS
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddStudents;
